import { useEffect, useRef, useState } from "react";
import { WHY_BOOKMARK_TEXT } from "../constants";

type RecommendationsToolbarProps = {
  selectedCount: number;
  onModifyClick: () => void;
  onBulkBookmark: () => void;
  onClearSelection: () => void;
};

const BUTTON_CLASS = "tw-flex tw-h-8 tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] hover:tw-bg-gray-50";

// "Why should you bookmark?" toggles its explanation on click; closes on outside click or Escape.
// The box opens to the left, its top edge level with the label.
const WhyBookmarkHint = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="tw-relative">
      {/* Design: "Why should you" regular, "bookmark" semibold purple, "?" medium. */}
      <button type="button" onClick={() => setOpen((prev) => !prev)} aria-expanded={open} className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-normal tw-tracking-[-0.07px] tw-text-gray-900">
        Why should you<span className="tw-font-medium"> </span>
        <span className="tw-font-semibold tw-text-primary-purple-600">bookmark</span>
        <span className="tw-font-medium">?</span>
      </button>
      {open && (
        <div role="tooltip" className="tw-absolute tw-right-full tw-top-0 tw-z-50 tw-mr-2 tw-w-[484px] tw-rounded-lg tw-border tw-border-[#fee0c3] tw-bg-[#fff9f3] tw-p-3 tw-text-sm tw-text-gray-900">
          {WHY_BOOKMARK_TEXT}
        </div>
      )}
    </div>
  );
};

const RecommendationsToolbar = ({ selectedCount, onModifyClick, onBulkBookmark, onClearSelection }: RecommendationsToolbarProps) => (
  <div className="tw-flex tw-h-8 tw-items-center tw-justify-between tw-gap-4">
    <div className="tw-flex tw-items-center tw-gap-3">
      <button type="button" onClick={onModifyClick} className={`${BUTTON_CLASS} tw-text-gray-900`}>
        <span className="material-symbols-outlined tw-flex tw-size-5 tw-items-center tw-justify-center tw-text-lg tw-leading-none tw-text-black">edit</span>
        Modify recommendation criteria
      </button>
      {selectedCount > 0 && (
        <>
          <button type="button" onClick={onBulkBookmark} className={`${BUTTON_CLASS} tw-text-primary-purple-600`}>
            <span className="material-symbols-outlined tw-flex tw-size-5 tw-items-center tw-justify-center tw-text-lg tw-leading-none">star</span>
            Bookmark selected ({selectedCount})
          </button>
          <button type="button" onClick={onClearSelection} className="tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-gray-500 tw-underline hover:tw-text-gray-900">
            Clear
          </button>
        </>
      )}
    </div>

    <WhyBookmarkHint />
  </div>
);

export default RecommendationsToolbar;
